import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { usePrevious } from '../../hooks/usePrevious';
import { type DateTimePickerProps } from './DateTimePicker.types';

export interface UseDateTimePickerRangeProps extends Omit<
  DateTimePickerProps,
  'label' | 'errorText' | 'hintText' | 'selectsStart' | 'selectsEnd' | 'value' | 'onChange'
> {
  onChange?: (range: [Date | undefined, Date | undefined]) => void;
  maxDurationMs?: number;
}

interface State {
  start: Date | undefined;
  end: Date | undefined;
}

/**
 * useDateTimePickerRange is a hook to make it easy to render a pair of DateTimePickers
 * to pick a time range.  This will take care of ensuring that the start time isn't
 * later than the end time, and simplifies passing props.
 *
 * See DateTimePicker.stories.tsx#DateTimeRange() for an example.
 */
export function useDateTimePickerRange(
  props: UseDateTimePickerRangeProps,
): [DateTimePickerProps, DateTimePickerProps, Date | undefined, Date | undefined] {
  const { startTime, endTime, onChange, maxDurationMs, ...rest } = props;
  const [state, setState] = useState({ start: startTime, end: endTime });

  const prevStartTime = usePrevious(startTime);
  const prevEndTime = usePrevious(endTime);

  // If the passed in startTime/endTime change, update their values.
  useEffect(() => {
    if (prevStartTime?.valueOf() !== startTime?.valueOf()) {
      setState((state) => ({ ...state, start: startTime }));
    }
    if (prevEndTime?.valueOf() !== endTime?.valueOf()) {
      setState((state) => ({ ...state, end: endTime }));
    }
  }, [startTime, prevStartTime, endTime, prevEndTime]);

  const updateStateAndNotify = useCallback(
    (fn: (state: State) => State) => {
      setState((oldState) => {
        const newState = fn(oldState);
        if (newState !== oldState) {
          onChange?.([newState.start, newState.end]);
        }
        return newState;
      });
    },
    [onChange],
  );

  const updateStart = useCallback(
    (start: Date | undefined) => updateStateAndNotify((state) => ({ ...state, start })),
    [updateStateAndNotify],
  );

  const updateEnd = useCallback(
    (end: Date | undefined) => updateStateAndNotify((state) => ({ ...state, end })),
    [updateStateAndNotify],
  );

  // Only fix the start/end dates when the control loses focus, otherwise things get
  // finicky when you're trying to type in a date or time.
  const onBlurStart = useCallback(
    () => updateStateAndNotify((state) => fixStartEnd(state, maxDurationMs, 'start')),
    [maxDurationMs, updateStateAndNotify],
  );

  const onBlurEnd = useCallback(
    () => updateStateAndNotify((state) => fixStartEnd(state, maxDurationMs, 'end')),
    [maxDurationMs, updateStateAndNotify],
  );

  // Don't show a "range picker" for the dates if the maxDuration is short, because
  // it looks like you can pick a week worth of days, but as soon as you try it
  // will shrink back down to a single day (or two days).
  const showAsRange = !maxDurationMs || maxDurationMs > moment.duration(1, 'day').asMilliseconds();

  return [
    {
      value: state.start,
      onChange: updateStart,
      onBlur: onBlurStart,
      startTime: showAsRange ? state.start : undefined,
      endTime: showAsRange ? state.end : undefined,
      selectsStart: showAsRange,
      ...rest,
    },
    {
      value: state.end,
      onChange: updateEnd,
      onBlur: onBlurEnd,
      startTime: showAsRange ? state.start : undefined,
      endTime: showAsRange ? state.end : undefined,
      selectsEnd: showAsRange,
      ...rest,
    },
    state.start,
    state.end,
  ];
}

function fixStartEnd(
  state: Readonly<State>,
  maxDurationMs: number | undefined,
  startOrEnd: 'start' | 'end',
): State {
  let newState: State = state;

  if (!state.start || !state.end) {
    newState = state;
  } else if (state.end < state.start) {
    // If the end is before the start, correct them to be the same.

    let { start: newStart, end: newEnd } = state;
    if (startOrEnd === 'start') {
      newEnd = state.start;
    } else {
      newStart = state.end;
    }

    newState = { start: newStart, end: newEnd };
  } else if (maxDurationMs && state.end.valueOf() - state.start.valueOf() > maxDurationMs) {
    // If the duration is too long, fix it.
    let { start: newStart, end: newEnd } = state;

    if (startOrEnd === 'start') {
      newEnd = new Date(newStart.valueOf() + maxDurationMs);
    } else {
      newStart = new Date(newEnd.valueOf() - maxDurationMs);
    }

    newState = { start: newStart, end: newEnd };
  }

  return newState;
}
